import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { sortedListRequest } from './common/list.js';
import { paginated } from './common/pagination.js';
import { generatedFileSchema } from './generated-file.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
};

export const AUDIT_ACTOR_KINDS = ['user', 'system'] as const;

export type AuditActorKind = (typeof AUDIT_ACTOR_KINDS)[number];

export const auditEntrySchema = z.object({
  id: z.uuid(),
  actorKind: z.enum(AUDIT_ACTOR_KINDS),
  actorRef: z.string().nullable(),
  userId: z.uuid().nullable(),
  impersonatedBy: z.uuid().nullable().optional(),
  userName: z.string().nullable(),
  userEmail: z.string().nullable(),
  entity: z.string(),
  entityId: z.uuid().nullable(),
  action: z.string(),
  oldValue: z.record(z.string(), z.unknown()).nullable(),
  newValue: z.record(z.string(), z.unknown()).nullable(),
  ip: z.string().nullable(),
  createdAt: z.string(),
});

/**
 * One column, because the trail has one axis. `entity` and `action` are filters
 * rather than orderings: a trail read in any order but time is not a trail, and
 * neither column would be answering a question the filters do not already.
 */
export const AUDIT_SORT_FIELDS = ['createdAt'] as const;

export type AuditSortField = (typeof AUDIT_SORT_FIELDS)[number];

/**
 * The filter an audit export is generated from: the viewer's own, without its
 * page. An export is every matching row, so a page number would be a lie.
 */
export const auditExportInputSchema = z.object({
  sortBy: z.enum(AUDIT_SORT_FIELDS).default('createdAt'),
  sortDir: z.enum(['asc', 'desc']).default('desc'),
  entity: z.string().trim().optional(),
  action: z.string().trim().optional(),
  userId: z.uuid().optional(),
  entityId: z.uuid().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
});

export type AuditExportInput = z.infer<typeof auditExportInputSchema>;

export const auditContract = {
  list: oc
    .route({ method: 'GET', path: '/audit-logs', summary: 'Immutable trail of who changed what' })
    .errors(guarded)
    .input(
      sortedListRequest(AUDIT_SORT_FIELDS, 'createdAt', 'desc').omit({ search: true }).extend({
        entity: z.string().trim().optional(),
        action: z.string().trim().optional(),
        userId: z.uuid().optional(),
        entityId: z.uuid().optional(),
        from: z.string().optional(),
        to: z.string().optional(),
      }),
    )
    .output(paginated(auditEntrySchema)),

  /**
   * The whole filter as a file the worker writes — a compliance request asks
   * for every row, not a page of them, so it is always a job and never a
   * synchronous download (docs/decisions/0043-the-worker.md §7). It carries its
   * own permission, `auditLog.export`, beside `auditLog.list`: reading the trail
   * on a screen and carrying it out of the system are two different acts.
   */
  export: oc
    .route({ method: 'POST', path: '/audit-logs/export' })
    .errors(guarded)
    .input(auditExportInputSchema)
    .output(generatedFileSchema),

  /** The distinct entities and actions present, so filters offer real choices. */
  facets: oc
    .route({ method: 'GET', path: '/audit-logs/facets' })
    .errors(guarded)
    .input(z.object({}))
    .output(
      z.object({
        entities: z.array(z.string()),
        actions: z.array(z.string()),
      }),
    ),
};
